/**
 * BranchingChoicesEditor
 *
 * P_MULTIPLE_CHOICE_BRANCHING_SURVEYS_1.0 — per-choice editor that
 * appears inside a Survey Editor question card when the question's
 * `response_type` is `multiple_choice_branching`.
 *
 * Each choice has:
 *   - id            stable, generated client-side, never edited
 *   - label.{en|fr|es}
 *   - next_step.type            "next" | "jump" | "end"
 *   - next_step.target_question_id   (only when type === "jump")
 *
 * The choice list itself can be reordered (move up / move down),
 * appended to (Add choice), and items removed (×) — but never below
 * 2 items, so the question always remains a valid branch.
 *
 * Intentionally simple: per the spec ("Simple. No visual flowcharts.
 * No complexity."), there is no graph view. Just a list of choices,
 * each with three plain inputs and a Next Step group.
 */
import { useTranslation } from 'react-i18next';
import { ArrowDown, ArrowUp, Plus, Trash2, Bell } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Switch } from '../../../components/ui/switch';
import { Checkbox } from '../../../components/ui/checkbox';
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../../components/ui/select';

const MIN_CHOICES = 2;

const genChoiceId = () =>
    `c_${Math.random().toString(36).slice(2, 10)}`;

export const defaultBranchingChoices = () => ([
    { id: genChoiceId(), label: { en: '', fr: '', es: '' }, next_step: { type: 'next' } },
    { id: genChoiceId(), label: { en: '', fr: '', es: '' }, next_step: { type: 'next' } },
]);

const BranchingChoicesEditor = ({ question, questionIdx, allQuestions, onChange, onCreateBranch, authoringLang = 'en', alertEnabled = false }) => {
    const { t } = useTranslation();
    const choices = question.choices?.length ? question.choices : defaultBranchingChoices();

    // Available jump targets = every question in the survey except
    // this one. We accept jumps both forward AND backward; the public
    // runtime guards against infinite loops with a visited set.
    const jumpTargets = (allQuestions || [])
        .filter((q) => q.id !== question.id)
        .map((q) => ({
            id: q.id,
            label: q.text?.en || q.text?.fr || q.text?.es || q.id,
            order: q.order ?? 0,
        }));

    const setChoices = (next) => onChange(next);

    const updateChoice = (idx, patch) => {
        const next = choices.map((c, i) => (i === idx ? { ...c, ...patch } : c));
        setChoices(next);
    };

    const updateChoiceLabel = (idx, lang, value) => {
        const next = choices.map((c, i) =>
            i === idx ? { ...c, label: { ...(c.label || {}), [lang]: value } } : c
        );
        setChoices(next);
    };

    const updateNextStep = (idx, patch) => {
        const next = choices.map((c, i) => {
            if (i !== idx) return c;
            const ns = { ...(c.next_step || { type: 'next' }), ...patch };
            // Clear target_question_id when leaving "jump" so we never
            // persist a stale id that would later fail server validation.
            if (ns.type !== 'jump') delete ns.target_question_id;
            return { ...c, next_step: ns };
        });
        setChoices(next);
    };

    const addChoice = () => {
        setChoices([
            ...choices,
            { id: genChoiceId(), label: { en: '', fr: '', es: '' }, next_step: { type: 'next' } },
        ]);
    };

    // P_BRANCHING_OTHER_CHOICE_1.0 — an "Other" choice is an ordinary choice
    // with allows_text already on and the label pre-filled in all three
    // languages, so nothing has to be translated later.
    const addOtherChoice = () => {
        setChoices([
            ...choices,
            {
                id: genChoiceId(),
                label: {
                    en: t('surveys.branching.otherLabel.en', 'Other'),
                    fr: t('surveys.branching.otherLabel.fr', 'Autre'),
                    es: t('surveys.branching.otherLabel.es', 'Otro'),
                },
                next_step: { type: 'next' },
                allows_text: true,
            },
        ]);
    };

    const removeChoice = (idx) => {
        if (choices.length <= MIN_CHOICES) return;
        setChoices(choices.filter((_, i) => i !== idx));
    };

    const moveChoice = (idx, dir) => {
        const swap = idx + dir;
        if (swap < 0 || swap >= choices.length) return;
        const next = [...choices];
        [next[idx], next[swap]] = [next[swap], next[idx]];
        setChoices(next);
    };

    return (
        <div
            className="space-y-3 rounded-md border border-dashed border-slate-300 dark:border-slate-700 bg-slate-50/60 dark:bg-slate-900/40 p-3"
            data-testid={`q-branching-editor-${questionIdx}`}
            data-insight-id={questionIdx === 0 ? "surveys.workflow.branching-choices" : undefined}
            data-insight-label={questionIdx === 0 ? "Ask a different follow-up per answer" : undefined}
        >
            <div className="flex items-center justify-between">
                <Label className="text-xs font-semibold uppercase tracking-wide text-slate-600 dark:text-slate-300">
                    {t('surveys.branching.choicesLabel', 'Answer choices & paths')}
                </Label>
                <span className="text-xs text-slate-500" data-testid={`q-branching-count-${questionIdx}`}>
                    {t('surveys.branching.choiceCount', { count: choices.length, defaultValue: '{{count}} choices' })}
                </span>
            </div>

            {choices.map((c, ci) => (
                <div
                    key={c.id}
                    className="rounded-md border border-slate-200 dark:border-slate-700 bg-white/80 dark:bg-slate-950/40 p-3 space-y-2"
                    data-testid={`q-branching-choice-${questionIdx}-${ci}`}
                >
                    <div className="flex items-center justify-between gap-2">
                        <span className="text-xs font-mono text-slate-500">{c.id}</span>
                        <div className="flex items-center gap-1">
                            <Button
                                size="icon"
                                variant="ghost"
                                onClick={() => moveChoice(ci, -1)}
                                disabled={ci === 0}
                                data-testid={`q-branching-up-${questionIdx}-${ci}`}
                                aria-label={t('surveys.branching.moveUp', 'Move up')}
                            >
                                <ArrowUp className="w-4 h-4" />
                            </Button>
                            <Button
                                size="icon"
                                variant="ghost"
                                onClick={() => moveChoice(ci, +1)}
                                disabled={ci === choices.length - 1}
                                data-testid={`q-branching-down-${questionIdx}-${ci}`}
                                aria-label={t('surveys.branching.moveDown', 'Move down')}
                            >
                                <ArrowDown className="w-4 h-4" />
                            </Button>
                            <Button
                                size="icon"
                                variant="ghost"
                                onClick={() => removeChoice(ci)}
                                disabled={choices.length <= MIN_CHOICES}
                                className="text-red-500"
                                data-testid={`q-branching-delete-${questionIdx}-${ci}`}
                                aria-label={t('surveys.branching.removeChoice', 'Remove choice')}
                            >
                                <Trash2 className="w-4 h-4" />
                            </Button>
                        </div>
                    </div>

                    <div>
                        {/* P_AUTHORING_PARITY_1.0 — single-language authoring
                            for branching choice labels. Author types in their
                            chosen language; backend translates the other two
                            on save (services/translation_service.py). */}
                        <Input
                            value={c.label?.[authoringLang] || ''}
                            onChange={(e) => updateChoiceLabel(ci, authoringLang, e.target.value)}
                            placeholder={t(`surveys.branching.labelPlaceholder.${authoringLang}`, 'Choice label')}
                            data-testid={`q-branching-label-${questionIdx}-${ci}`}
                        />
                    </div>

                    {/* Alert this answer — only while the question's switch is ON. */}
                    {alertEnabled && (
                        <label className="flex items-center gap-2 text-xs cursor-pointer rounded-md border border-dashed px-2.5 py-1.5">
                            <Checkbox
                                checked={!!c.alert_on_choice}
                                onCheckedChange={(v) => updateChoice(ci, { alert_on_choice: !!v })}
                                data-testid={`q-branching-alert-choice-${questionIdx}-${ci}`}
                            />
                            <Bell className="w-3 h-3 text-amber-500" />
                            <span className="text-muted-foreground">
                                {t('surveys.answerAlert.choiceLabel', 'Send an alert when this is chosen')}
                            </span>
                        </label>
                    )}

                    {/* Let the guest type their own answer under this choice. */}
                    <div className="flex items-center justify-between gap-3 rounded-md border border-dashed px-2.5 py-1.5">
                        <Label className="text-xs text-muted-foreground leading-snug">
                            {t('surveys.branching.allowsText', 'Let the guest type their own answer')}
                        </Label>
                        <Switch
                            checked={!!c.allows_text}
                            onCheckedChange={(v) => updateChoice(ci, { allows_text: !!v })}
                            data-testid={`q-branching-allows-text-${questionIdx}-${ci}`}
                        />
                    </div>

                    {/* Next Step config */}
                    <div className="grid grid-cols-1 sm:grid-cols-[160px_1fr] gap-2 items-start">
                        <Select
                            value={c.next_step?.type || 'next'}
                            onValueChange={(v) => {
                                // P_BRANCHING_AUTHORING_TREE_1.0 —
                                // "create_new_branch" is a UX shortcut,
                                // NOT a persisted next_step type. When
                                // the author picks it we delegate to
                                // the parent which adds a new question
                                // and rewrites this choice's next_step
                                // to a real `jump` pointing at it.
                                if (v === 'create_new_branch') {
                                    if (typeof onCreateBranch === 'function') {
                                        onCreateBranch(ci);
                                    }
                                    return;
                                }
                                updateNextStep(ci, { type: v });
                            }}
                        >
                            <SelectTrigger data-testid={`q-branching-nextstep-type-${questionIdx}-${ci}`}>
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="next">{t('surveys.branching.next', 'Continue to next question')}</SelectItem>
                                <SelectItem value="jump">{t('surveys.branching.jump', 'Jump to existing question')}</SelectItem>
                                <SelectItem value="end">{t('surveys.branching.end', 'End survey (submit answers)')}</SelectItem>
                                <SelectItem value="create_new_branch" data-testid={`q-branching-create-branch-opt-${questionIdx}-${ci}`} data-insight-id={questionIdx === 0 && ci === 0 ? "surveys.workflow.create-new-branch" : undefined} data-insight-label={questionIdx === 0 && ci === 0 ? "Create New Branch" : undefined}>
                                    ✨ {t('surveys.branching.createNewBranch', 'Create New Branch')}
                                </SelectItem>
                            </SelectContent>
                        </Select>

                        {c.next_step?.type === 'jump' && (
                            <Select
                                value={c.next_step?.target_question_id || ''}
                                onValueChange={(v) => updateNextStep(ci, { target_question_id: v })}
                            >
                                <SelectTrigger data-testid={`q-branching-jump-target-${questionIdx}-${ci}`}>
                                    <SelectValue placeholder={t('surveys.branching.selectTarget', 'Select a question…')} />
                                </SelectTrigger>
                                <SelectContent>
                                    {jumpTargets.length === 0 && (
                                        <SelectItem value="__none__" disabled>
                                            {t('surveys.branching.noTargets', 'No other questions yet')}
                                        </SelectItem>
                                    )}
                                    {jumpTargets.map((q, qi) => (
                                        <SelectItem key={q.id} value={q.id}>
                                            Q{qi + 1} — {q.label}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        )}
                    </div>
                </div>
            ))}

            <Button
                variant="outline"
                size="sm"
                onClick={addChoice}
                data-testid={`q-branching-add-choice-${questionIdx}`}
            >
                <Plus className="w-4 h-4 mr-1.5" />
                {t('surveys.branching.addChoice', 'Add choice')}
            </Button>
            <Button
                variant="outline"
                size="sm"
                onClick={addOtherChoice}
                className="ml-2"
                data-testid={`q-branching-add-other-choice-${questionIdx}`}
            >
                <Plus className="w-4 h-4 mr-1.5" />
                {t('surveys.branching.addOtherChoice', "Add 'Other' choice")}
            </Button>
        </div>
    );
};

export default BranchingChoicesEditor;
